import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { lastValueFrom, RmsMessageService, PromptService } from '@services';
import { RmsModule } from '@rms';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { FuturesDataService, FuturesExchange, FuturesMonthOption } from '../../../../../services/futures-data.service';
import { confirmPrice, priceUnitWarning } from '../futures-shared';

const DEFAULT_CONTRACT_NOTE = 'To be applied to physical coffee before First Notice Day.';

@Component({
  selector: 'app-futures-contract-form',
  imports: [RmsModule],
  templateUrl: './futures-contract-form.component.html',
  styleUrl: './futures-contract-form.component.scss'
})
export class FuturesContractFormComponent implements OnInit {
  Loading = true;
  Saving = false;

  Customers: any[] = [];
  Traders: { TraderID: number; FullName: string }[] = [];
  Months: (FuturesMonthOption & { Label: string })[] = [];
  Exchanges: FuturesExchange[] = [];
  Terminals: { TerminalID: number; TerminalName: string }[] = [];

  CompanyID: number | null = null;
  TraderID: number | null = null;
  FutureCompositeID: number | null = null;
  WeightLbs: number | null = null;
  Price: number | null = null;
  PurchaseDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 10);
  TerminalID: number | null = null;
  ContractNote = DEFAULT_CONTRACT_NOTE;

  constructor(
    private futuresData: FuturesDataService,
    private msg: RmsMessageService,
    private prompt: PromptService,
    private pulldownRef: RmsPulldownRef,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    try {
      const [customers, traders, lookups, terminals] = await Promise.all([
        lastValueFrom(this.futuresData.getCustomers()),
        lastValueFrom(this.futuresData.getTraders()),
        lastValueFrom(this.futuresData.getLookups()),
        lastValueFrom(this.futuresData.getTerminals())
      ]);
      this.Customers = customers ?? [];
      this.Traders = (traders ?? []).filter(t => t.IsActive !== false).sort((a, b) => a.FullName.localeCompare(b.FullName));
      this.Exchanges = lookups?.Exchanges ?? [];
      this.Months = (lookups?.Months ?? []).map(m => ({
        ...m,
        Label: `${m.FutureCompositeName}${m.FND ? ' · FND ' + new Date(m.FND.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-US') : ''}`
      }));
      this.Terminals = (terminals ?? [])
        .filter(t => t.TerminalName && t.TerminalName.trim())
        .map(t => ({ TerminalID: t.TerminalID, TerminalName: t.TerminalName!.trim() }))
        .sort((a, b) => a.TerminalName.localeCompare(b.TerminalName));
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load the form lists.');
    } finally {
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  get exchange(): FuturesExchange | null {
    const month = this.Months.find(m => m.FutureCompositeID === this.FutureCompositeID);
    return month ? this.Exchanges.find(e => e.ExchangeID === month.Exchange) ?? null : null;
  }

  get contractWeight(): number {
    return Number(this.exchange?.ContractWeight) || 37500;
  }

  get unitLabel(): string {
    return this.exchange?.PriceUnit === 'MT' ? 'MT' : 'lb';
  }

  get lots(): number {
    return this.WeightLbs ? this.WeightLbs / this.contractWeight : 0;
  }

  get canSave(): boolean {
    return !!this.CompanyID && !!this.FutureCompositeID && !!this.WeightLbs && this.WeightLbs > 0 && !this.Saving;
  }

  onMonthChanged(id: number | null) {
    this.FutureCompositeID = id;
    // default to one full contract on that exchange
    if (!this.WeightLbs) this.WeightLbs = this.contractWeight;
  }

  setContracts(count: number) {
    this.WeightLbs = Math.round(count * this.contractWeight * 100) / 100;
  }

  get priceWarning(): string | null {
    return this.exchange ? priceUnitWarning(this.Price, this.exchange.PriceUnit) : null;
  }

  async save() {
    if (!this.canSave) return;
    if (this.priceWarning && !(await confirmPrice(this.prompt, this.priceWarning))) return;
    this.Saving = true;
    try {
      const result = await lastValueFrom(this.futuresData.createContract({
        CompanyID: this.CompanyID!,
        TraderID: this.TraderID,
        FutureCompositeID: this.FutureCompositeID!,
        WeightLbs: Number(this.WeightLbs),
        Price: this.Price != null && String(this.Price) !== '' ? Number(this.Price) : null,
        PurchaseDate: this.PurchaseDate || null,
        TerminalID: this.TerminalID,
        ContractNote: this.ContractNote.trim() || null
      }));

      const n = result.Notification;
      const note = !n ? ''
        : n.delivered ? ` Risk Manager notified (${n.recipients}).`
        : n.pending ? ' Risk Management will be notified once a Risk Manager is set up.'
        : ` The Risk Manager notification has not gone out yet: ${n.error}`;
      this.prompt.open({
        promptText: `Futures contract FC-${result.FuturesPositionID} created.${note}`,
        buttons: [{ label: 'OK', value: true, buttonClass: 'btn-bg-blue' }]
      });
      this.pulldownRef.close(result.FuturesPositionID);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not create the futures contract.');
    } finally {
      this.Saving = false;
      this.cdr.markForCheck();
    }
  }

  cancel() {
    this.pulldownRef.close(null);
  }
}
